/*
Description: Reads the Prompt scheduler for Sync Cal (COM-546).
The calendar's visits live in Prompt's Pinia store (scheduling.selectedWeekData,
keyed provider -> day), and the Acct# only comes from Prompt's patient API, so
main_world.js reads both for us.
Date: 2026-10-18
*/

// Prompt's visit ChargeType, labelled as on the scheduler's day counts.
const CANCELLED_LABELS: Record<string, string> = {
  C: 'Patient Canceled',
  CC: 'Center Canceled',
  N: 'No Show'
};

// Prompt slices the day into 5-minute blocks.
const BLOCK_MINUTES = 5;

export async function getSchedule() {
  const state = await askMainWorld('getPromptSchedule');
  if (!state) {
    throw new Error(
      'Open your schedule in Prompt, then click Sync Schedule again.'
    );
  }

  const date = getCalendarDate(state);
  const providerId = pickProvider(state);
  const visits = (
    state.visits?.[providerId]?.[date.replace(/-/g, '/')] || []
  ).filter((visit: any) => !visit.deleted_at);

  const patientIds = [
    ...new Set(visits.map((visit: any) => visit.PatientId).filter(Boolean))
  ];
  const accounts =
    (patientIds.length &&
      (await askMainWorld('getPromptAccounts', { patientIds }))) ||
    {};

  const rows = visits.map((visit: any) =>
    mapVisit(visit, date, state.providers?.[providerId], accounts)
  );
  const isCancelled = (row: any) =>
    Boolean(CANCELLED_LABELS[row.details?.chargeType]);

  return {
    date,
    appointments: rows.filter((row: any) => !row.skipped && !isCancelled(row)),
    cancelled: rows.filter((row: any) => !row.skipped && isCancelled(row)),
    skipped: rows.filter((row: any) => row.skipped)
  };
}

function askMainWorld(action: string, args = {}): Promise<any> {
  const reqId = `${action}_${Date.now()}`;
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(null);
    }, 10000);

    const handler = (event: MessageEvent) => {
      if (
        event.source === window &&
        event.data?.action === `${action}_response` &&
        event.data.reqId === reqId
      ) {
        clearTimeout(timeoutId);
        window.removeEventListener('message', handler);
        resolve(event.data.result || null);
      }
    };

    window.addEventListener('message', handler);
    window.postMessage({ action, reqId, ...args }, '*');
  });
}

// Day view syncs the day on screen; week view only if today is in it.
function getCalendarDate(state: any): string {
  if (state.view === 'Day' && state.selectedDate) return state.selectedDate;

  const today = localDate(new Date());
  const weekStart = String(state.weekStart || '').replace(/\//g, '-');
  if (state.view === 'Week' && weekStart) {
    const [y, m, d] = weekStart.split('-').map(Number);
    const weekEnd = localDate(new Date(y, m - 1, d + 6));
    if (weekStart <= today && today <= weekEnd) return today;
  }
  throw new Error(
    'Switch your Prompt calendar to Day view, then click Sync Schedule again.'
  );
}

// The scheduler can show several providers; only sync the user's own column.
function pickProvider(state: any): string {
  const selected: string[] = state.selectedProviderIds || [];
  if (state.employeeId && selected.includes(state.employeeId)) {
    return state.employeeId;
  }
  if (selected.length === 1) return selected[0];
  throw new Error(
    'Filter the Prompt scheduler to your own schedule, then click Sync Schedule again.'
  );
}

function mapVisit(
  visit: any,
  date: string,
  provider: string | undefined,
  accounts: Record<string, string>
) {
  const scheduledAt = toTimestamp(date, visit.block);
  const firstName = clean(visit.FirstName);
  const lastName = clean(visit.LastName);

  if (!visit.PatientId) {
    return { skipped: true, scheduledAt, reason: 'not a patient visit' };
  }
  if (!firstName || !lastName) {
    return {
      skipped: true,
      scheduledAt,
      reason: "couldn't read the patient name"
    };
  }

  const chargeType = clean(visit.ChargeType);
  const status =
    CANCELLED_LABELS[chargeType] || (chargeType ? 'Arrived' : 'Scheduled');
  const durationMinutes = getDurationMinutes(visit);

  return {
    appointmentId: visit.VisitId ? String(visit.VisitId) : undefined,
    firstName,
    lastName,
    dob: clean(visit.DateOfBirth) || undefined,
    // Acct#, which PromptInject.getPatientInfo uses as the EMR id.
    emrId: accounts[visit.PatientId] || undefined,
    date,
    scheduledAt,
    noteType: clean(visit.Name) || undefined,
    provider: provider || undefined,
    status,
    details: {
      promptPatientId: visit.PatientId ?? null,
      promptVisitId: visit.VisitId ?? null,
      caseId: visit.CaseId ?? null,
      injury: visit.Injury ?? null,
      visitType: visit.Name ?? null,
      status,
      chargeType: visit.ChargeType ?? null,
      stage: visit.Stage ?? null,
      payerType: visit.Type ?? null,
      placeOfService: visit.PlaceOfService ?? null,
      nickName: visit.NickName ?? null,
      startAt: scheduledAt ?? null,
      durationMinutes,
      nextVisitStart: visit.nextVisitStart ?? null,
      visitSeriesId: visit.VisitSeriesId ?? null,
      isPreCheckedIn: visit.isPreCheckedIn ?? null
    }
  };
}

// endBlock is the visit's last block, inclusive.
function getDurationMinutes(visit: any): number | null {
  const blocks = Number(visit.endBlock) - Number(visit.block) + 1;
  return Number.isFinite(blocks) && blocks > 0 ? blocks * BLOCK_MINUTES : null;
}

// Blocks are clinic-local; conversations store UTC without the Z.
function toTimestamp(date: string, block: any): string | undefined {
  const minutes = Number(block) * BLOCK_MINUTES;
  if (!Number.isFinite(minutes) || minutes < 0) return undefined;
  const [year, month, day] = date.split('-').map(Number);
  const start = new Date(year, month - 1, day, 0, minutes);
  if (Number.isNaN(start.getTime())) return undefined;
  return start.toISOString().slice(0, -1);
}

function localDate(value: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

function clean(value: any): string {
  return String(value ?? '').trim();
}
